<template>
	<teleport to="#click-wrapper">
		<div class="quest-chronicle-story__container">
			<div class="quest-chronicle-story__skip-all-wrapper">
				<LabeledDecorBox
					:text="translate('button.skip')"
					:style="{ opacity: hasDoubleTaped ? 1 : 0 }"
					icon="skip-all"
					@click="skipAll"
				/>
			</div>
			<div
				@click="handleQuestStorySkip"
				class="quest-chronicle-story"
				:style="{
					'--story-background-image': storyBackgroundName
						? `url('${pathImages}/story/narratives/backgrounds/${isMobile() ? 'portrait' : 'landscape'}/${storyBackgroundName}.avif') no-repeat left center / contain`
						: undefined,
				}"
			>
				<div class="quest-chronicle-story__wrapper">
					<div class="quest-chronicle-story__character-wrapper">
						<div
							class="quest-chronicle-story__character"
							:class="[
								characterImage,
								`quest-chronicle-story__character--${currentNarrative?.data?.direction}`,
								{
									'quest-chronicle-story__character--behind':
										characterImage &&
										characterName === '{username}',
								},
							]"
						/>
					</div>
					<div class="quest-chronicle-story__content">
						<BidirectionalTextLabel
							v-if="characterName"
							:text="playerName"
							:theme="
								characterName === '{username}'
									? LabelThemes.BLUE
									: characterName === 'character.narrator'
										? LabelThemes.GREY
										: LabelThemes.RED
							"
							class="quest-chronicle-story__character-name"
							:class="[
								characterName === '{username}'
									? 'quest-chronicle-story__character-name--right'
									: characterName === 'character.narrator'
										? 'quest-chronicle-story__character-name--center'
										: currentNarrative?.data?.direction ===
											  NarrativeDirection.LEFT
											? 'quest-chronicle-story__character-name--left'
											: 'quest-chronicle-story__character-name--right',
							]"
						/>
						<span
							ref="storyText"
							class="quest-chronicle-story__text"
						>
							{{ storyTextValue }}
						</span>
						<span
							class="quest-chronicle-story__continue"
							:class="{
								'quest-chronicle-story__continue--glow': isIdle,
							}"
						>
							{{ translate("ui.tap_to_continue") }}
						</span>
					</div>
					<svg
						class="quest-chronicle-story__background"
						width="100%"
						height="100%"
					>
						<defs>
							<pattern
								id="repeatable-pattern"
								width="432"
								height="100%"
								patternUnits="userSpaceOnUse"
							>
								<RaggedNarrativeBackground />
							</pattern>
						</defs>
						<rect
							x="0"
							y="0"
							width="100%"
							height="100%"
							fill="url(#repeatable-pattern)"
						/>
					</svg>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script lang="ts" setup>
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import LabeledDecorBox from "@/components/SVG/LabeledDecorBox.vue";
import RaggedNarrativeBackground from "@/components/SVG/RaggedNarrativeBackground.vue";
import SoundService from "@/game/SoundService";
import useQuestStore from "@/store/useQuestStore";
import useUserStore from "@/store/useUserStore";
import { NarrativeDirection } from "@/types/quest";
import { LabelThemes } from "@/types/ui";
import { isMobile } from "@/utils/isMobile";
import { translate } from "@/utils/translate";
import { pathImages } from "@/utils/url";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	onBeforeUnmount,
	onMounted,
	ref,
	useTemplateRef,
	watch,
} from "vue";

const soundService = markRaw(SoundService.getInstance());

const userStore = useUserStore();
const useQuests = useQuestStore();
const { currentNarrative } = storeToRefs(useQuests);

const storyText = useTemplateRef<HTMLElement>("storyText");
const storyTextValue = ref<string>("");
const storyBackgroundName = ref<string | null>(null);

const characterName = computed(
	(): string | null => currentNarrative.value?.data?.character_name ?? null,
);

const characterImage = computed((): string | null =>
	currentNarrative.value?.data?.image
		? `${currentNarrative.value?.data?.image.toLowerCase()}-${
				currentNarrative.value?.data?.direction ===
				NarrativeDirection.LEFT
					? "l"
					: "r"
			}`
		: null,
);

const playerName = computed((): string => {
	if (characterName.value === "{username}") {
		if (
			userStore.userName.length &&
			!userStore.userName.startsWith("Anonymous.")
		)
			return userStore.userName;
		return translate("character.player.non_translatable");
	}
	return characterName.value ? translate(characterName.value) : "";
});

const lastTap = ref<number>(0);
const hasDoubleTaped = ref<boolean>(false);
const doubleTapDelay = 300; // ms - double tap/hold key sensitivity

const handleQuestStorySkip = (): void => {
	const now = new Date().getTime();
	const timeDiff = now - lastTap.value;

	// NOTE: Universal control for double-mouse/key-action
	// based on the number of calls
	if (timeDiff < doubleTapDelay && timeDiff > 0 && !hasDoubleTaped.value)
		hasDoubleTaped.value = true;

	if (!currentNarrative.value?.order_number) return;
	useQuests.nextNarrativeChapter(currentNarrative.value.order_number + 1);
	soundService.playGenerickClick();

	lastTap.value = now;
};

const skipAll = (): void => {
	soundService.playGenerickClick();
	// NOTE: Force skip since the quest is already applied for the user during narrative display, so we can simply end the narrative
	useQuests.stopNarrative();
};

const handleKeyPress = (event: KeyboardEvent): void => {
	if (["Enter", " "].includes(event.key)) {
		// NOTE: Internal method has a universal control for double-mouse/key-action
		// NOTE: No further action control is needed
		handleQuestStorySkip();
	}
};

watch(
	(): string | null => currentNarrative.value?.data?.text ?? null,
	(newValue: string | null, oldValue: string | null): void => {
		if (!storyText.value || !(storyText.value instanceof HTMLElement))
			return;
		if (oldValue !== null) {
			storyBackgroundName.value =
				currentNarrative.value?.data?.backgroundImage ?? null;
			storyText.value.style.transition = "opacity 0.5s ease-in-out";
			storyText.value.style.opacity = "0";
			setTimeout((): void => {
				if (
					!storyText.value ||
					!(storyText.value instanceof HTMLElement)
				)
					return;

				storyTextValue.value = translate(newValue ?? "");
				storyText.value.style.opacity = "1";
			}, 500); // Delay for fade-out before fade-in
		} else {
			storyBackgroundName.value =
				currentNarrative.value?.data?.backgroundImage ?? null;
			storyTextValue.value = translate(newValue ?? "");
			storyText.value.style.opacity = "1";
		}
	},
);

onMounted((): void => {
	window.addEventListener("keydown", handleKeyPress);
	storyTextValue.value = translate(currentNarrative.value?.data?.text ?? "");
	storyBackgroundName.value =
		currentNarrative.value?.data?.backgroundImage ?? null;
});

onBeforeUnmount((): void => {
	window.removeEventListener("keydown", handleKeyPress);
});

if (currentNarrative === null) {
	useQuests.stopNarrative();
}

const isIdle = ref<boolean>(false);
let idleTimeout: ReturnType<typeof setTimeout> | null = null;

const resetIdleTimer = (): void => {
	if (idleTimeout !== null) clearTimeout(idleTimeout);
	isIdle.value = false;
	idleTimeout = setTimeout((): void => {
		isIdle.value = true;
	}, 4000); // NOTE: 4 sec. IDLE CTA
};

watch((): string => storyTextValue.value, resetIdleTimer, { immediate: true });
</script>

<style lang="scss" scoped>
.quest-chronicle-story {
	position: absolute;
	bottom: 0;
	left: 0;
	z-index: 10;
	width: 100%;
	height: 100%;
	@include flex-col-end;

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		background: rgba(0, 0, 0, 0.6);
		@include background(
			var(--story-background-image),
			cover,
			center,
			no-repeat
		);
		z-index: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		content: "";
	}

	&__container {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 10;
		width: 100%;
		height: 100%;
		// IMPORTANT: Android scaling fix for Samsung A52s and S24+
		overflow: hidden;
	}

	&__skip-all-wrapper {
		@include flex-row-end;
		padding: calc(var(--safe-area-inset-top) + 0.125rem) 0.5rem 0;

		.labeled-decor-box {
			z-index: 12;
		}
	}

	&__wrapper {
		position: relative;
		width: 100%;
		height: 15.5rem;
	}

	&__content {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		z-index: 12;
		padding: 2.5rem 0 1.25rem;
		width: 100%;
		max-width: 27rem;
		height: 100%;
		@include flex-col-center;
		justify-content: flex-start;

		.bidirectional-text-label {
			position: absolute;
			top: -0.5rem;
			width: 16.625rem;
		}

		.quest-chronicle-story {
			&__text {
				flex: 1 1 auto;
				padding: 0 1.125rem;
				width: 100%;
				color: #372615;
				font-weight: 800;
				font-size: 1rem;
				line-height: 1.5rem;
				text-align: left;
			}

			&__continue {
				color: #372615;
				font-size: 1rem;
				text-transform: uppercase;

				&--glow {
					transform-origin: center center;
					animation: fadeScale 1.5s ease-in-out infinite;
				}

				@keyframes fadeScale {
					0%,
					100% {
						transform: scale(1);
						opacity: 0.3;
					}
					50% {
						transform: scale(0.97);
						opacity: 1;
					}
				}
			}
		}
	}

	&__character {
		position: absolute;
		bottom: calc(100% - 1rem);
		z-index: 0;
		filter: drop-shadow(0.356rem 0.356rem 0.75rem rgba(0, 0, 0, 0.5));
		transition: all 0.6s cubic-bezier(0.25, 1, 0.5, 1);
		background-position: bottom center;
		width: 24.375rem;
		height: 27rem; // primary character height during speech

		&--behind {
			width: 19.625rem;
			height: 21.625rem;
		}

		&-wrapper {
			position: absolute;
			left: 50%;
			transform: translateX(-50%);
			width: 100%;
			max-width: 27rem;
			height: 100%;
		}

		&--right {
			right: -10%;
			overflow: hidden;
		}

		&--left {
			left: -10%;
			overflow: hidden;
		}

		&-name {
			&--left {
				left: 0;
			}

			&--right {
				right: 0;
			}

			&--center {
				left: 50%;
				transform: translateX(-50%);
			}
		}
	}

	&__background {
		position: relative;
		z-index: 11;
	}
}
</style>
